import { Event } from '../types/Event';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface WhatsAppTemplate {
  id: string;
  name: string;
  description: string;
  variables: TemplateVariable[];
  previewTemplate: string;
}

interface TemplateVariable {
  key: string;
  label: string;
  required: boolean;
  defaultValue?: string;
}

// Simple template getter using API
const getTemplateSynx = (templateId: string): WhatsAppTemplate | null => {
  // This is a fallback - in practice, templates should be fetched from API
  const basicTemplates: Record<string, WhatsAppTemplate> = {
    'join-event': { 
      id: 'join-event', 
      name: '報名確認', 
      description: '參加者報名活動後的確認訊息',
      variables: [
        { key: 'participantName', label: '參加者姓名', required: true },
        { key: 'customMessage', label: '自訂訊息', required: true }
      ],
      previewTemplate: '你好 {{participantName}}，{{customMessage}}'
    },
    'payment': { 
      id: 'payment', 
      name: '付款通知', 
      description: '發送付款連結給參加者',
      variables: [
        { key: 'participantName', label: '參加者姓名', required: true },
        { key: 'paymentLink', label: '付款連結', required: true }
      ],
      previewTemplate: '你好 {{participantName}}，請使用以下連結付款：{{paymentLink}}'
    },
    'broadcast': { 
      id: 'broadcast', 
      name: '廣播訊息', 
      description: '發送廣播訊息給所有參加者',
      variables: [
        { key: 'participantName', label: '參加者姓名', required: true },
        { key: 'customMessage', label: '廣播訊息', required: true }
      ],
      previewTemplate: '你好 {{participantName}}，{{customMessage}}'
    },
    'reminder': { 
      id: 'reminder', 
      name: '活動提醒', 
      description: '使用廣播模板發送活動提醒',
      variables: [
        { key: 'participantName', label: '參加者姓名', required: true },
        { key: 'customMessage', label: '提醒內容', required: true }
      ],
      previewTemplate: '你好 {{participantName}}，{{customMessage}}'
    }
  };
  return basicTemplates[templateId] || null;
};

export interface Participant {
  _id: string;
  name: string;
  phone: string;
  spots: number;
  fee: number;
  checkedIn: boolean;
  paid: boolean;
  approved: boolean;
}

export interface SiteSettings {
  paymentLink?: string;
  defaultSenderId?: string;
  defaultSenderName?: string;
  whatsappMessageTemplate?: string;
  // Add other settings as needed
}

export interface TemplateData {
  participant: Participant;
  event: Event;
  siteSettings: SiteSettings;
  siteName: string;
  customMessage?: string;
}

// Cache for generated reminder messages to avoid repeated API calls
const reminderMessageCache = new Map<string, string>();

/**
 * Generate reminder message using backend API
 */
const generateReminderMessage = async (event: Event, contactNumber: string): Promise<string> => {
  const cacheKey = `${event._id}_${event.time}_${event.location}_${contactNumber}`;
  
  // Check cache first
  if (reminderMessageCache.has(cacheKey)) {
    return reminderMessageCache.get(cacheKey)!;
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/templates/reminder/generate`, {
      eventData: {
        time: event.time,
        location: event.location,
        sendDetails: event.sendDetails
      },
      contactNumber
    });

    const message = response.data.message;
    reminderMessageCache.set(cacheKey, message);
    return message;
  } catch (error) {
    console.error('Error fetching reminder message from API, using template fallback:', error);
    // Fallback to template-based format if API fails
    const template = getTemplateSynx('reminder');
    let fallbackMessage = '提提您：明天{{eventTime}}於{{eventLocation}}{{eventDetails}}。如有任何問題，請隨時聯繫{{contactNumber}}。謝謝！';
    
    if (template && template.previewTemplate) { // Changed to previewTemplate for fallback
      fallbackMessage = template.previewTemplate;
    }
    
    // Replace template variables with actual data
    fallbackMessage = fallbackMessage
      .replace(/\{\{eventTime\}\}/g, event.time || '')
      .replace(/\{\{eventLocation\}\}/g, event.location || '')
      .replace(/\{\{eventDetails\}\}/g, event.sendDetails || '')
      .replace(/\{\{contactNumber\}\}/g, contactNumber || '');
    
    reminderMessageCache.set(cacheKey, fallbackMessage);
    return fallbackMessage;
  }
};

export const renderTemplate = (templateId: string, data: TemplateData): string => {
  const template = getTemplateSynx(templateId);
  if (!template) {
    throw new Error(`Template with ID '${templateId}' not found`);
  }

  const variableMapping = buildVariableMapping(template, data, templateId);
  return renderTemplateString(template.previewTemplate, variableMapping);
};

const buildVariableMapping = (template: WhatsAppTemplate, data: TemplateData, templateId?: string): Record<string, string> => {
  const { participant, event, siteSettings, siteName, customMessage } = data;
  const mapping: Record<string, string> = {};

  // Map variables based on the specific template
  template.variables.forEach(variable => {
    switch (variable.key) {
      // Common participant information
      case 'participantName':
        mapping[variable.key] = participant.name || '';
        break;

      // Event information
      case 'eventDate':
        mapping[variable.key] = event.date || '';
        break;
      case 'eventTime':
        mapping[variable.key] = event.time || '';
        break;
      case 'eventLocation':
        mapping[variable.key] = event.location || '';
        break;
      case 'eventDetails':
        mapping[variable.key] = event.sendDetails || '';
        break;
      case 'eventFee':
        mapping[variable.key] = participant.fee ? `$${participant.fee}` : '免費';
        break;

      // Contact and site information
      case 'contactNumber':
        mapping[variable.key] = siteSettings.defaultSenderId || '';
        break;
      case 'siteName':
        mapping[variable.key] = siteName || '';
        break;

      // Payment information
      case 'paymentLink':
        mapping[variable.key] = siteSettings.paymentLink || '';
        break;

      // Custom content
      case 'customMessage':
        // For reminder template, we'll handle this asynchronously
        mapping[variable.key] = customMessage || '';
        break;

      default:
        mapping[variable.key] = variable.defaultValue || '';
        break;
    }
  });

  return mapping;
};

/**
 * Async version of renderTemplate for reminder messages
 */
export const renderTemplateAsync = async (templateId: string, data: TemplateData): Promise<string> => {
  const template = getTemplateSynx(templateId);
  if (!template) {
    throw new Error(`Template with ID '${templateId}' not found`);
  }

  let variableMapping = buildVariableMapping(template, data, templateId);

  // For reminder template without custom message, fetch from backend
  if (templateId === 'reminder' && !data.customMessage) {
    const reminderMessage = await generateReminderMessage(data.event, data.siteSettings.defaultSenderId || '');
    variableMapping['customMessage'] = reminderMessage;
  }

  return renderTemplateString(template.previewTemplate, variableMapping);
};

const renderTemplateString = (template: string, variables: Record<string, string>): string => {
  let rendered = template;
  
  // Replace all {{variableName}} placeholders
  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    rendered = rendered.replace(placeholder, value || '');
  });
  
  return rendered;
};

export const validateTemplateData = (templateId: string, data: TemplateData): { isValid: boolean; missingVariables: string[] } => {
  const template = getTemplateSynx(templateId);
  if (!template) {
    return { isValid: false, missingVariables: [`Template '${templateId}' not found`] };
  }

  const variableMapping = buildVariableMapping(template, data, templateId);
  const missingVariables: string[] = [];

  template.variables.forEach(variable => {
    if (variable.required && !variableMapping[variable.key]) {
      missingVariables.push(variable.label);
    }
  });

  return {
    isValid: missingVariables.length === 0,
    missingVariables
  };
};

export interface PreviewData {
  templateId: string;
  participants: Participant[];
  event: Event;
  siteSettings: SiteSettings;
  siteName: string;
  customMessage?: string;
}

export const generateMultipleTemplatePreviews = (data: PreviewData) => {
  const { templateId, participants, event, siteSettings, siteName, customMessage } = data;

  return participants.map(participant => {
    try {
      const templateData: TemplateData = {
        participant,
        event,
        siteSettings,
        siteName,
        customMessage
      };

      const preview = renderTemplate(templateId, templateData);
      const validation = validateTemplateData(templateId, templateData);

      return {
        participant,
        preview,
        isValid: validation.isValid,
        missingVariables: validation.missingVariables
      };
    } catch (error) {
      return {
        participant,
        preview: `Error generating preview: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isValid: false,
        missingVariables: ['Template rendering error']
      };
    }
  });
};

/**
 * Async version for generating multiple template previews
 */
export const generateMultipleTemplatePreviewsAsync = async (data: PreviewData) => {
  const { templateId, participants, event, siteSettings, siteName, customMessage } = data;

  const previews = await Promise.all(participants.map(async participant => {
    try {
      const templateData: TemplateData = {
        participant,
        event,
        siteSettings,
        siteName,
        customMessage
      };

      const preview = await renderTemplateAsync(templateId, templateData);
      const validation = validateTemplateData(templateId, templateData);

      return {
        participant,
        preview,
        isValid: validation.isValid,
        missingVariables: validation.missingVariables
      };
    } catch (error) {
      return {
        participant,
        preview: `Error generating preview: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isValid: false,
        missingVariables: ['Template rendering error']
      };
    }
  }));

  return previews;
};

export const getTemplateInfo = (templateId: string) => {
  const template = getTemplateSynx(templateId);
  if (!template) return null;

  return {
    name: template.name,
    description: template.description,
    variables: template.variables,
    usageScenarios: [], // No usage scenarios in this simplified version
    twilioContentSid: undefined // No twilioContentSid in this simplified version
  };
}; 